import { brandName, guestAppConfig } from '@tov-plus/config';
import { type GuestTheme } from '@tov-plus/domain';
import { OfflineBanner, PwaInstallAction } from '@tov-plus/pwa';
import {
  PageLayout,
  PageTitleBreadcrumb,
  LocaleSwitcher,
  TovIcon,
  TovMetaBadge,
  TovMenu,
  TovMenuItem,
  TovMenuList,
  TovMenuSeparator,
  msg,
  TovIconButton,
} from '@tov-plus/ui';
import { EnvironmentTag } from '@zektec/environment-tag';
import { useEffect, type CSSProperties } from 'react';
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from '@tov-plus/frontend-data';

import { buildGuestNavigation } from '../app/navigation';
import {
  getGuestResumeStorageKey,
  shouldPersistGuestPath,
} from '../app/access';
import { guestEnv } from '../config/env';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { getLocaleDirection } from '../lib/localization/guestLocale';

export function resolveWebsiteNavPath(item: {
  target_path: string | null;
  item_key: string;
}) {
  if (item.target_path) {
    return item.target_path;
  }
  if (item.item_key === 'events') {
    return '/events';
  }
  if (item.item_key === 'locations') {
    return '/locations';
  }
  if (item.item_key === 'rsvp') {
    return '/rsvp';
  }
  if (item.item_key === 'forms') {
    return '/forms';
  }
  if (item.item_key === 'registry') {
    return '/registry';
  }
  if (item.item_key === 'gallery') {
    return '/gallery';
  }
  if (item.item_key === 'schedule') {
    return '/schedule';
  }

  return '/';
}

function resolveShellWidth(containerWidthPreference: string | undefined) {
  switch (containerWidthPreference) {
    case 'content-1200':
      return 'min(1200px, calc(100vw - 2rem))';
    case 'content-1180':
      return 'min(1180px, calc(100vw - 2rem))';
    case 'content-1120':
    default:
      return 'min(1120px, calc(100vw - 2rem))';
  }
}

function resolvePanelPadding(input: {
  spacingScale: string | undefined;
  componentSizePreference: string | undefined;
  cardDensityPreference: string | undefined;
}) {
  if (
    input.spacingScale === 'balanced' ||
    input.cardDensityPreference === 'compact'
  ) {
    return input.componentSizePreference === 'medium' ? '0.9rem' : '0.82rem';
  }

  return '1.08rem';
}

function resolveGridGap(input: {
  spacingScale: string | undefined;
  sectionGapScale: string | undefined;
}) {
  if (
    input.spacingScale === 'cozy' &&
    input.sectionGapScale === 'comfortable'
  ) {
    return '0.95rem';
  }

  return '0.82rem';
}

function resolveSectionGap(sectionGapScale: string | undefined) {
  return sectionGapScale === 'comfortable' ? '1.05rem' : '0.82rem';
}

function resolveBaseFontSize(fontScale: string | undefined) {
  return fontScale === 'compact' ? '0.9rem' : '0.975rem';
}

function resolveLineHeight(lineHeightPreset: string | undefined) {
  switch (lineHeightPreset) {
    case 'relaxed':
      return '1.72';
    case 'airy':
      return '1.82';
    case 'balanced':
    default:
      return '1.5';
  }
}

function resolveHeadingLineHeight(lineHeightPreset: string | undefined) {
  switch (lineHeightPreset) {
    case 'relaxed':
      return '1.16';
    case 'airy':
      return '1.22';
    case 'balanced':
    default:
      return '1.08';
  }
}

function resolveLetterSpacing(input: {
  letterSpacingPreset: string | undefined;
  textTransformPreference: string | undefined;
}) {
  if (input.textTransformPreference === 'uppercase-labels') {
    return '0.12em';
  }

  switch (input.letterSpacingPreset) {
    case 'tight':
      return '0.03em';
    case 'soft':
      return '0.08em';
    case 'balanced':
    default:
      return '0.06em';
  }
}

function resolveHeadingLetterSpacing(input: {
  letterSpacingPreset: string | undefined;
  textTransformPreference: string | undefined;
}) {
  if (input.textTransformPreference === 'smallcaps-headings') {
    return '0.08em';
  }

  switch (input.letterSpacingPreset) {
    case 'tight':
      return '-0.015em';
    case 'soft':
      return '0.02em';
    case 'balanced':
    default:
      return '0';
  }
}

function resolveLabelTextTransform(
  textTransformPreference: string | undefined,
) {
  return textTransformPreference === 'none' ? 'none' : 'uppercase';
}

function resolveHeadingVariantCaps(
  textTransformPreference: string | undefined,
) {
  return textTransformPreference === 'smallcaps-headings'
    ? 'small-caps'
    : 'normal';
}

function resolveMotionScale(input: {
  animationLevel: string | undefined;
  transitionDurationProfile: string | undefined;
}) {
  if (
    input.animationLevel === 'minimal' ||
    input.transitionDurationProfile === 'fast'
  ) {
    return {
      fast: '110ms',
      normal: '160ms',
      slow: '220ms',
    };
  }

  if (
    input.animationLevel === 'subtle' ||
    input.transitionDurationProfile === 'gentle'
  ) {
    return {
      fast: '170ms',
      normal: '240ms',
      slow: '320ms',
    };
  }

  return {
    fast: '200ms',
    normal: '280ms',
    slow: '360ms',
  };
}

function resolveHeroRadius(
  imageCornerTreatment: string | undefined,
  borderRadius: string | undefined,
) {
  if (imageCornerTreatment === 'rounded') {
    return borderRadius ?? '20px';
  }

  return '999px';
}

function resolveHeroWash(input: {
  accentSoft: string;
  imageOverlayStrength: string | undefined;
  gradientUsage: string | undefined;
  defaultHeroTreatment: string | undefined;
}) {
  const opacity = Number.parseFloat(input.imageOverlayStrength ?? '0.28');

  if (
    input.defaultHeroTreatment === 'image-with-confetti-gradient' ||
    input.gradientUsage === 'strong' ||
    input.gradientUsage === 'expressive'
  ) {
    return `color-mix(in srgb, ${input.accentSoft} 85%, white)`;
  }

  if (
    input.defaultHeroTreatment === 'image-structured-overlay' ||
    input.defaultHeroTreatment === 'image-strong-overlay'
  ) {
    return `color-mix(in srgb, ${input.accentSoft} 78%, white)`;
  }

  return Number.isFinite(opacity) && opacity >= 0.3
    ? `color-mix(in srgb, ${input.accentSoft} 72%, white)`
    : `color-mix(in srgb, ${input.accentSoft} 48%, white)`;
}

function resolveHoverLift(hoverIntensity: string | undefined) {
  return hoverIntensity === 'soft' ? 'translateY(-1px)' : 'translateY(-2px)';
}

function escapeCssImageUrl(url: string) {
  return `url("${url.replaceAll('\\', '\\\\').replaceAll('"', '\\"')}")`;
}

export function resolveShellStyle(designTheme: GuestTheme) {
  const motionScale = resolveMotionScale({
    animationLevel: designTheme.motion.animationLevel,
    transitionDurationProfile: designTheme.motion.transitionDurationProfile,
  });

  return {
    '--tov-accent': designTheme.colors.accent,
    '--tov-accent-strong': designTheme.colors.accentStrong,
    '--tov-accent-soft': designTheme.colors.accentSoft,
    '--tov-page-background': designTheme.colors.background,
    '--tov-surface-primary': designTheme.colors.surface,
    '--tov-surface-secondary': designTheme.colors.surfaceSecondary,
    '--tov-surface-muted': `color-mix(in srgb, ${designTheme.colors.surfaceSecondary} 82%, white)`,
    '--tov-sidebar-from': designTheme.colors.sidebarFrom,
    '--tov-sidebar-to': designTheme.colors.sidebarTo,
    '--tov-sidebar-ink': `color-mix(in srgb, ${designTheme.colors.surface} 24%, ${designTheme.colors.text})`,
    '--tov-ink-strong': designTheme.colors.text,
    '--tov-ink-soft': designTheme.colors.mutedText,
    '--tov-border': designTheme.colors.border,
    '--tov-border-strong': designTheme.colors.borderStrong,
    '--tov-heading-font-family': designTheme.typography.headingFontFamily,
    '--tov-body-font-family': designTheme.typography.bodyFontFamily,
    '--tov-accent-font-family': designTheme.typography.accentFontFamily,
    '--tov-font-display': designTheme.typography.headingFontFamily,
    '--tov-font-body': designTheme.typography.bodyFontFamily,
    '--tov-radius': designTheme.layout.borderRadius,
    '--tov-shadow': designTheme.components.card.shadow,
    '--tov-shadow-panel': designTheme.components.card.shadow,
    '--tov-panel-padding': resolvePanelPadding({
      spacingScale: designTheme.layout.spacingScale,
      componentSizePreference: designTheme.layout.componentSizePreference,
      cardDensityPreference: designTheme.layout.cardDensityPreference,
    }),
    '--tov-grid-gap': resolveGridGap({
      spacingScale: designTheme.layout.spacingScale,
      sectionGapScale: designTheme.layout.sectionGapScale,
    }),
    '--tov-motion-fast': motionScale.fast,
    '--tov-motion-normal': motionScale.normal,
    '--tov-motion-slow': motionScale.slow,
    '--guest-shell-width': resolveShellWidth(
      designTheme.layout.containerWidthPreference,
    ),
    '--guest-section-gap': resolveSectionGap(
      designTheme.layout.sectionGapScale,
    ),
    '--guest-hero-radius': resolveHeroRadius(
      designTheme.media.imageCornerTreatment,
      designTheme.layout.borderRadius,
    ),
    '--guest-hero-wash': resolveHeroWash({
      accentSoft: designTheme.colors.accentSoft,
      imageOverlayStrength: designTheme.media.imageOverlayStrength,
      gradientUsage: designTheme.media.gradientUsage,
      defaultHeroTreatment: designTheme.media.defaultHeroTreatment,
    }),
    '--guest-base-font-size': resolveBaseFontSize(
      designTheme.typography.fontScale,
    ),
    '--guest-body-line-height': resolveLineHeight(
      designTheme.typography.lineHeightPreset,
    ),
    '--guest-heading-line-height': resolveHeadingLineHeight(
      designTheme.typography.lineHeightPreset,
    ),
    '--guest-label-letter-spacing': resolveLetterSpacing({
      letterSpacingPreset: designTheme.typography.letterSpacingPreset,
      textTransformPreference: designTheme.typography.textTransformPreference,
    }),
    '--guest-heading-letter-spacing': resolveHeadingLetterSpacing({
      letterSpacingPreset: designTheme.typography.letterSpacingPreset,
      textTransformPreference: designTheme.typography.textTransformPreference,
    }),
    '--guest-label-text-transform': resolveLabelTextTransform(
      designTheme.typography.textTransformPreference,
    ),
    '--guest-heading-variant-caps': resolveHeadingVariantCaps(
      designTheme.typography.textTransformPreference,
    ),
    '--guest-button-radius': designTheme.components.button.radius,
    '--guest-hover-lift': resolveHoverLift(
      designTheme.components.button.hoverIntensity,
    ),
    '--guest-background-dimming': designTheme.media.backgroundImageDimming,
    '--guest-hero-image': designTheme.assets.hero.url
      ? escapeCssImageUrl(designTheme.assets.hero.url)
      : 'none',
    '--guest-decorative-image': designTheme.assets.decorative.url
      ? escapeCssImageUrl(designTheme.assets.decorative.url)
      : 'none',
  } as CSSProperties;
}

export function isFocusedRsvpRoute(pathname: string) {
  return pathname === '/rsvp';
}

function normalizePathname(pathname: string) {
  if (pathname === '/') {
    return pathname;
  }

  return pathname.replace(/\/+$/, '');
}

function resolveGuestPageTitle(
  pathname: string,
  navigationSections: Array<{
    label: string;
    items: Array<{
      href: string;
      label: string;
    }>;
  }>,
  isFocusedRsvpExperience: boolean,
) {
  if (isFocusedRsvpExperience) {
    return msg('general.modules.rsvp.name');
  }

  const normalizedPathname = normalizePathname(pathname);
  const navigationItem = navigationSections
    .flatMap((section) =>
      section.items.map((item) => ({
        ...item,
        section,
      })),
    )
    .find((item) => normalizePathname(item.href) === normalizedPathname);

  if (navigationItem) {
    return navigationItem.label;
  }

  if (normalizedPathname.startsWith('/events/')) {
    return (
      <PageTitleBreadcrumb
        current={msg('pages.event_detail_page.event_details')}
        root={{
          href: '/events',
          label: msg('general.modules.events.name'),
        }}
      />
    );
  }

  if (normalizedPathname === '/travel-packet') {
    return (
      <PageTitleBreadcrumb
        current={msg('pages.today_page.pages_today_page_travel_packet')}
        root={{
          href: '/locations',
          label: msg('general.modules.locations.name'),
        }}
      />
    );
  }

  if (normalizedPathname === '/review') {
    return (
      <PageTitleBreadcrumb
        current={msg(
          'pages.reviewer_preview_page.pages_reviewer_preview_page_reviewer_preview',
        )}
        root={{
          href: '/access',
          label: msg('app.navigation.access'),
        }}
      />
    );
  }

  return msg('app.navigation.overview');
}

export function AppShell() {
  const access = useGuestAccess();
  const location = useLocation();
  const environmentPillTone = guestEnv.environmentPill?.tone;
  const isLocalEnvironment =
    environmentPillTone === 'local' ||
    environmentPillTone === 'development' ||
    environmentPillTone === 'test';
  const isStagingEnvironment = environmentPillTone === 'stage';
  const showUnknownEnvironmentTag = environmentPillTone === 'unknown';
  const navigate = useNavigate();

  useEffect(() => {
    if (access.status !== 'ready') {
      return;
    }

    if (
      access.entryMode === 'invitation_rsvp' &&
      location.pathname !== '/rsvp'
    ) {
      navigate('/rsvp', { replace: true });
      return;
    }

    if (location.pathname === '/' && access.entryRedirect.path !== '/') {
      navigate(access.entryRedirect.path, { replace: true });
      access.consumeEntryRedirect();
    }
  }, [access, location.pathname, navigate]);

  useEffect(() => {
    if (access.status !== 'ready') {
      return;
    }

    if (!shouldPersistGuestPath(location.pathname)) {
      return;
    }

    window.localStorage.setItem(
      getGuestResumeStorageKey(access.occasionSlug, access.guestToken),
      location.pathname,
    );
  }, [access, location.pathname]);

  if (access.status !== 'ready') {
    return null;
  }

  const experience = access.experience;
  if (!experience) {
    return null;
  }

  const isInvitationRsvpEntry = access.entryMode === 'invitation_rsvp';
  const isFocusedRsvpExperience =
    isInvitationRsvpEntry || isFocusedRsvpRoute(location.pathname);
  const isPublicLandingRoute =
    experience.accessMode === 'public_visitor' &&
    normalizePathname(location.pathname) === '/';
  const navigationSections = buildGuestNavigation(
    experience.journeySummary.availableTargets,
    experience.journeySummary.attentionTarget,
    experience.journeySummary.recommendedTarget,
    experience.multiLanguageCapability.enabled,
  );
  const localeCode = experience.localizationContext.resolvedLocaleCode;
  const websiteNavItems = experience.guestWebsite.nav_items.map((item) => ({
    id: `website-${item.id}`,
    label: item.label,
    href: resolveWebsiteNavPath(item),
    summary: item.item_key,
  }));
  const pageTitleSections = isFocusedRsvpExperience
    ? []
    : websiteNavItems.length > 0
      ? [
          {
            id: 'website',
            label: msg('components.app_shell.website'),
            items: websiteNavItems,
          },
          ...navigationSections,
        ]
      : navigationSections;
  const pageTitle = resolveGuestPageTitle(
    location.pathname,
    pageTitleSections,
    isFocusedRsvpExperience,
  );
  const designTheme = experience.designTheme;
  const shellStyle = designTheme ? resolveShellStyle(designTheme) : undefined;
  const shellDensity =
    designTheme?.layout.cardDensityPreference === 'compact'
      ? 'simple'
      : 'advanced';
  const guestInstallExperience = {
    banner: {
      eyebrow: msg('components.app_shell.save_invitation'),
      title: msg(
        'components.app_shell.components_app_shell_add_this_occasion_to_your_home_screen',
      ),
      description: msg(
        'components.app_shell.components_app_shell_keep_your_invitation_schedule_and_updates_one_tap_away',
      ),
    },
    button: {
      label: msg('components.app_shell.save_invitation'),
      installingLabel: msg(
        'components.app_shell.components_app_shell_saving_invitation',
      ),
      installedLabel: msg(
        'components.app_shell.components_app_shell_invitation_saved',
      ),
    },
    ios: {
      description: msg(
        'components.app_shell.components_app_shell_on_iphone_save_this_invitation_from_safari_to_keep_it_on_your_home_screen',
      ),
      shareStep: msg(
        'components.app_shell.components_app_shell_tap_share_in_safari',
      ),
      addStep: msg(
        'components.app_shell.components_app_shell_choose_add_to_home_screen_for_this_invitation',
      ),
      hint: msg(
        'components.app_shell.components_app_shell_the_home_screen_icon_reopens_this_occasion_like_an_app',
      ),
    },
    modal: {
      title: msg('components.app_shell.save_invitation'),
      description: msg(
        'components.app_shell.components_app_shell_save_this_invitation_for_quick_guest_access',
      ),
    },
  };

  return (
    <div
      className={`guest-shell${isFocusedRsvpExperience ? ' guest-shell--invitation-only' : ''}${isPublicLandingRoute ? ' guest-shell--landing' : ''}`}
      data-tov-surface="guest"
      data-tov-density={shellDensity}
      data-tov-tone={designTheme?.presentationTone}
      data-tov-theme-id={designTheme?.themeId}
      data-tov-theme-version={designTheme?.version}
      lang={localeCode}
      dir={getLocaleDirection(localeCode)}
      style={shellStyle}
    >
      <EnvironmentTag
        environment={import.meta.env.MODE}
        isLocal={isLocalEnvironment}
        isStaging={isStagingEnvironment}
        showInProduction={showUnknownEnvironmentTag}
        placement="bottom-center"
      />
      <div className="guest-main">
        <OfflineBanner />
        {isPublicLandingRoute ? null : (
          <header className="guest-topbar">
            <div className="guest-topbar__identity">
              <p>{experience.occasion.displayName}</p>
            </div>

            <div className="guest-topbar__actions">
              {!isFocusedRsvpExperience && designTheme ? (
                <TovMetaBadge
                  className="planner-badge"
                  headline={designTheme.themeName}
                  detail={designTheme.styleVariant.replace('_', ' ')}
                />
              ) : null}
              {isFocusedRsvpExperience ? null : (
                <TovMenu
                  className="guest-topbar__profile-menu"
                  contentClassName="guest-topbar__profile-menu-content"
                  renderTrigger={(triggerProps) => (
                    <TovIconButton
                      {...triggerProps}
                      aria-label={msg(
                        'components.app_shell.components_app_shell_view_account',
                      )}
                      className="topbar-icon-button"
                      icon="user"
                    />
                  )}
                >
                  <TovMenuList className="guest-topbar__profile-menu-list">
                    <div className="guest-topbar__language-menu-row">
                      <TovIcon name="globe" />
                      <LocaleSwitcher
                        label={msg(
                          'components.shell.components_shell_language_preference',
                        )}
                      />
                    </div>
                    <TovMenuSeparator />
                    <TovMenuItem asChild>
                      <NavLink to="/my-invite">
                        <TovIcon name="user" />
                        {msg('app.navigation.my_invite')}
                      </NavLink>
                    </TovMenuItem>
                    <TovMenuItem asChild>
                      <NavLink to="/recent-invites">
                        <TovIcon name="books" />
                        {msg('app.navigation.recent_invites')}
                      </NavLink>
                    </TovMenuItem>
                    <TovMenuItem asChild>
                      <NavLink to="/follow-up">
                        <TovIcon name="arrow-right" />
                        {msg('app.navigation.follow_up')}
                      </NavLink>
                    </TovMenuItem>
                    <TovMenuItem asChild>
                      <NavLink to="/help">
                        <TovIcon name="question" />
                        {msg('app.navigation.help')}
                      </NavLink>
                    </TovMenuItem>
                    <TovMenuItem asChild>
                      <NavLink to="/access">
                        <TovIcon name="key" />
                        {msg('app.navigation.access')}
                      </NavLink>
                    </TovMenuItem>
                    <PwaInstallAction
                      copy={guestInstallExperience}
                      renderAction={({ onAction }) => (
                        <TovMenuItem onClick={onAction}>
                          <TovIcon name="upload" />
                          {guestInstallExperience.button.label}
                        </TovMenuItem>
                      )}
                    />
                    <TovMenuSeparator />
                    <TovMenuItem asChild>
                      <a
                        href={guestAppConfig.marketingUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <TovIcon name="globe" />
                        {brandName}
                      </a>
                    </TovMenuItem>
                  </TovMenuList>
                </TovMenu>
              )}
            </div>
          </header>
        )}

        <main
          className={`guest-content${isPublicLandingRoute ? ' guest-content--landing' : ''}`}
          data-testid="guest-app-main"
        >
          {isPublicLandingRoute ? (
            <Outlet />
          ) : (
            <PageLayout title={pageTitle}>
              <Outlet />
            </PageLayout>
          )}
        </main>
      </div>
    </div>
  );
}
